import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { loadPdfDocument, renderPdfPageToCanvas } from '../utils/pdfDocument';
import TrainingFieldSignDialog from './TrainingFieldSignDialog';

const fieldTypeLabel = (type) => {
  if (type === 'initial') return 'Initial';
  if (type === 'signature') return 'Sign';
  if (type === 'date') return 'Date';
  return 'Check';
};

const roleLabel = (role) => (role === 'supervisor' ? 'Supervisor' : 'Employee');

const todayYmd = () => new Date().toLocaleDateString('en-CA');

const TrainingPdfViewer = ({
  pdfBlob,
  fields = [],
  fieldValues = {},
  onFieldChange,
  readOnly = false,
  activeRole = 'employee',
  placementMode = false,
  placementType = 'checkbox',
  onPlaceField,
  defaultSignName = '',
  maxWidth = 800,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState('');
  const [signField, setSignField] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const pdf = await loadPdfDocument(pdfBlob);
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        setPageNumber(1);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pdfBlob]);

  const renderPage = useCallback(async () => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return;
    setRendering(true);
    try {
      const { width, height } = await renderPdfPageToCanvas(pdf, pageNumber, canvas, { maxWidth });
      setPageSize({ width, height });
    } catch (err) {
      setError(err.message || 'Failed to render page');
    } finally {
      setRendering(false);
    }
  }, [pageNumber, maxWidth]);

  useEffect(() => {
    if (!loading && pdfRef.current) renderPage();
  }, [loading, pageNumber, renderPage]);

  const pageFields = fields.filter((f) => f.page_number === pageNumber);

  const isFieldEditable = (field) => {
    if (readOnly || placementMode) return false;
    return (field.signer_role || 'employee') === activeRole;
  };

  const handleCanvasClick = (e) => {
    if (!placementMode || readOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
    const isSmall = placementType === 'checkbox';
    onPlaceField?.({
      page_number: pageNumber,
      field_type: placementType,
      x_percent: Math.round(xPercent * 10) / 10,
      y_percent: Math.round(yPercent * 10) / 10,
      width_percent: isSmall ? 3 : (placementType === 'date' ? 12 : 18),
      height_percent: isSmall ? 3 : 6,
      is_required: true,
    });
  };

  const handleFieldClick = (field, e) => {
    e.stopPropagation();
    if (!isFieldEditable(field)) return;
    if (field.field_type === 'checkbox') {
      const current = fieldValues[field.id]?.checked || false;
      onFieldChange?.(field.id, { checked: !current });
      return;
    }
    if (field.field_type === 'date') {
      const current = fieldValues[field.id]?.dateValue;
      onFieldChange?.(field.id, { dateValue: current ? null : todayYmd() });
      return;
    }
    setSignField(field);
  };

  const handleSignSave = (signatureImage) => {
    if (!signField) return;
    onFieldChange?.(signField.id, { signatureImage });
    setSignField(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton
          size="small"
          disabled={pageNumber <= 1}
          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="body2">
          Page {pageNumber} of {pageCount}
          {rendering && ' (rendering…)'}
        </Typography>
        <IconButton
          size="small"
          disabled={pageNumber >= pageCount}
          onClick={() => setPageNumber((p) => Math.min(pageCount, p + 1))}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.100',
          p: 1,
        }}
      >
        <Box
          sx={{ position: 'relative', display: 'inline-block', cursor: placementMode ? 'crosshair' : 'default' }}
          onClick={handleCanvasClick}
        >
          <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%' }} />

          {pageSize.width > 0 && pageFields.map((field) => {
            const value = fieldValues[field.id] || {};
            const left = `${field.x_percent}%`;
            const top = `${field.y_percent}%`;
            const width = `${field.width_percent}%`;
            const height = `${field.height_percent}%`;
            const isCheckbox = field.field_type === 'checkbox';
            const isDate = field.field_type === 'date';
            const hasSignature = !!value.signatureImage;
            const isChecked = !!value.checked;
            const hasDate = !!value.dateValue;
            const editable = isFieldEditable(field);
            const isSupervisor = (field.signer_role || 'employee') === 'supervisor';
            const filled = hasSignature || isChecked || hasDate;

            return (
              <Box
                key={field.id || `${field.page_number}-${field.x_percent}-${field.y_percent}`}
                onClick={(e) => handleFieldClick(field, e)}
                sx={{
                  position: 'absolute',
                  left,
                  top,
                  width,
                  height,
                  minWidth: isCheckbox ? 20 : 60,
                  minHeight: isCheckbox ? 20 : 28,
                  border: placementMode ? '2px dashed' : '1px solid',
                  borderColor: placementMode
                    ? (isSupervisor ? 'secondary.main' : 'primary.main')
                    : (filled ? 'success.main' : (editable ? 'warning.main' : 'grey.400')),
                  bgcolor: placementMode
                    ? (isSupervisor ? 'rgba(156, 39, 176, 0.08)' : 'rgba(25, 118, 210, 0.08)')
                    : 'rgba(255, 255, 255, 0.75)',
                  borderRadius: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: editable ? 'pointer' : 'default',
                  overflow: 'hidden',
                  opacity: (!placementMode && !editable && !filled) ? 0.5 : 1,
                  pointerEvents: placementMode ? 'none' : 'auto',
                }}
                title={field.label || `${roleLabel(field.signer_role)} ${fieldTypeLabel(field.field_type)}`}
              >
                {placementMode && (
                  <Typography variant="caption" sx={{ fontSize: 9, px: 0.25, lineHeight: 1.1, textAlign: 'center' }}>
                    {roleLabel(field.signer_role)[0]}: {fieldTypeLabel(field.field_type)}
                  </Typography>
                )}
                {!placementMode && isCheckbox && (
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    disabled={!editable}
                    sx={{ p: 0 }}
                  />
                )}
                {!placementMode && isDate && (
                  <Typography variant="caption" sx={{ fontSize: 11, color: hasDate ? 'text.primary' : 'text.secondary' }}>
                    {hasDate ? value.dateValue : (editable ? 'Add date' : 'Date')}
                  </Typography>
                )}
                {!placementMode && !isCheckbox && !isDate && hasSignature && (
                  <Box
                    component="img"
                    src={value.signatureImage}
                    alt={field.field_type}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                )}
                {!placementMode && !isCheckbox && !isDate && !hasSignature && (
                  <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                    {fieldTypeLabel(field.field_type)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {!placementMode && pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          <Button size="small" disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => p - 1)}>
            Previous
          </Button>
          <Button size="small" disabled={pageNumber >= pageCount} onClick={() => setPageNumber((p) => p + 1)}>
            Next
          </Button>
        </Box>
      )}

      <TrainingFieldSignDialog
        open={!!signField}
        onClose={() => setSignField(null)}
        field={signField}
        defaultName={defaultSignName}
        onSave={handleSignSave}
      />
    </Box>
  );
};

export default TrainingPdfViewer;
